import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ultraadminBillingApi, type BillingInvoice } from '@/services/api/billing'
import { Button, Input } from '@/components/common'
import { extractErrorMessage } from '@/utils/errorHandler'

export function UltraadminInvoicesPage() {
  const [params] = useSearchParams()
  const [rows, setRows] = useState<BillingInvoice[]>([])
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState(params.get('status') || '')
  const [period, setPeriod] = useState(params.get('period') || '')
  const [amounts, setAmounts] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [dates, setDates] = useState<Record<string, string>>({})
  const [names, setNames] = useState<Record<string, BillingInvoice['lines']>>({})


  const load = () => {
    const q: Record<string, string> = {}
    if (status) q.status = status
    if (period) q.period = period
    const schoolId = params.get('school_id')
    if (schoolId) q.school_id = schoolId
    ultraadminBillingApi.invoices(q).then(setRows).catch((e) => setError(extractErrorMessage(e, { loadError: 'Fehler' })))
  }
  useEffect(() => { load() }, [status, period, params]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="text-2xl font-bold mb-4">Rechnungen</h1>
      {error && <p className="text-red-600">{error}</p>}
      <div className="flex gap-2 mb-4 flex-wrap items-end">
        <select className="border rounded px-2 py-1" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Status</option>
          <option value="open">offen</option>
          <option value="paid">bezahlt</option>
          <option value="credited">gutgeschrieben</option>
        </select>
        <Input placeholder="Periode YYYY-MM" value={period} onChange={(e) => setPeriod(e.target.value)} />
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b"><th className="py-2">Nr.</th><th>Schule</th><th>Periode</th><th>Betrag</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rows.map((inv) => (
            <tr key={inv.id} className="border-b align-top">
              <td className="py-2">{inv.number}</td>
              <td>{inv.school_name}</td>
              <td>{inv.period_month}/{inv.period_year}</td>
              <td>{inv.remaining} / {inv.gross} {inv.currency}</td>
              <td>{inv.status}</td>
              <td className="py-2">
                <div className="flex gap-2 flex-wrap items-center">
                  <span className="text-xs text-gray-500">{inv.qr_reference}</span>
                  <button className="underline" onClick={() => ultraadminBillingApi.downloadPdf(inv.id).catch((e) => setError(extractErrorMessage(e, { loadError: 'PDF' })))}>PDF</button>
                  <button className="underline" onClick={() => ultraadminBillingApi.invoice(inv.id).then((d) => setNames((n) => ({ ...n, [inv.id]: d.lines }))).catch(() => undefined)}>Userliste</button>
                  {(inv.status === 'open' || inv.status === 'paid') && (
                    <>
                      <Input className="w-24" value={amounts[inv.id] ?? ''} onChange={(e) => setAmounts((a) => ({ ...a, [inv.id]: e.target.value }))} placeholder="Betrag" />
                      {inv.status === 'open' && (
                        <>
                          <input type="date" className="border rounded px-1 py-1 text-xs" value={dates[inv.id] ?? ''} onChange={(e) => setDates((d) => ({ ...d, [inv.id]: e.target.value }))} />
                          <Input className="w-28" value={notes[inv.id] ?? ''} onChange={(e) => setNotes((n) => ({ ...n, [inv.id]: e.target.value }))} placeholder="Notiz" />
                          <Button size="sm" onClick={() => {
                            const received = dates[inv.id]
                            ultraadminBillingApi.markPaid(inv.id, Number(amounts[inv.id] || inv.remaining), notes[inv.id], received ? new Date(received).toISOString() : undefined).then(load)
                          }}>Eingang</Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => ultraadminBillingApi.credit(inv.id, Number(amounts[inv.id] || inv.remaining)).then(load)}>Gutschrift</Button>
                    </>
                  )}
                  {inv.status === 'credited' && (
                    <Button size="sm" variant="ghost" onClick={() => ultraadminBillingApi.reissue(inv.id).then(load)}>Neue Rechnung</Button>
                  )}
                </div>
                {names[inv.id]?.length ? (
                  <ul className="mt-2 text-xs">
                    {names[inv.id]!.map((ln, i) => <li key={i}>{ln.display_name} · {ln.fraction}</li>)}
                  </ul>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
